import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material'
import type { Path, RegisterOptions, UseFormReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import { capitalize } from '@/utils/helpers'

interface SelectOption<T extends string> {
  label: string
  value: T
}

interface Props<T extends object, S extends string> {
  name: Path<T>
  id: string
  label: string
  control: UseFormReturn<T>['control']
  rules: Omit<
    RegisterOptions<T, Path<T>>,
    'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >
  options: SelectOption<S>[]
}

export const getSelectFieldDisplayValue = (selected: string[]): string =>
  selected
    .map(capitalize)
    .sort((a, b) => a.localeCompare(b))
    .join(', ')

const MuiMultiSelectField = <T extends object, S extends string>({
  label,
  name,
  id,
  control,
  options,
  rules,
}: Props<T, S>) => {
  const labelId = `${id}-label`

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Controller<T>
        rules={rules}
        render={({ field }) => {
          const selectedValues = (Array.isArray(field.value) ? field.value : []) as S[]

          return (
            <Select
              {...field}
              multiple
              fullWidth
              labelId={labelId}
              id={id}
              name={name}
              label={label}
              renderValue={getSelectFieldDisplayValue}
            >
              {options.map(({ label, value }, i) => (
                <MenuItem value={value} key={i}>
                  <Checkbox checked={selectedValues.includes(value)} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          )
        }}
        control={control}
        name={name}
      />
    </FormControl>
  )
}

export default MuiMultiSelectField
